import { Button, ButtonTheme } from 'components/Button';
import { HeaderToolbar } from 'components/HeaderToolbar';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { Menu } from 'components/Menu';
import { Txt } from 'components/Txt';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SidebarShowButton } from 'componentsAware/SidebarShowButton';
import { useSelector } from 'hooks/useSelector';
import { pluralise } from 'lib/pluralise';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import * as React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL } from 'store/actions';
import {
  doEngageFlatsLoad,
  doEngageFlatsSetFilter,
} from 'store/remote/engageActions';
import {
  selectEngageFlatsFilterSearch,
  selectEngageFlatsFilterOccupationStatuses,
  selectEnagageFlatsFilterissueContactTypes,
  selectEngageFlatsRequiresResidentSupport,
} from 'store/remote/engageSelectors';
import {
  EngageFlatsFilter,
  EngagePropertyListItem,
  EngageFlatsOccupationStatusFilters,
} from 'types/Engage';
import { Highlight } from 'components/Highlight';
type Props = {
  isLoading: boolean;
  property: EngagePropertyListItem;
  onBackPress: () => void;
  onReloadPress: () => void;
  onOnboardFlatsPress: () => void;
  flatsCount: number;
  pageKey: string;
  filtersLocked?: boolean;
  hasBulkSelection?: boolean;
  onBulkSendPress?: () => void;
};

const allOccupationStatusIds = EngageFlatsOccupationStatusFilters.map(
  (status) => status.id,
);

export const EngageFlatsHeader: React.FC<Props> = ({
  isLoading,
  property,
  onBackPress,
  onReloadPress,
  onOnboardFlatsPress,
  flatsCount,
  pageKey,
  filtersLocked = false,
  hasBulkSelection = false,
  onBulkSendPress,
}) => {
  const dispatch = useDispatch();

  const searchValue = useSelector((state) =>
    selectEngageFlatsFilterSearch(state, pageKey),
  );
  const selectedOccupationStatuses = useSelector((state) =>
    selectEngageFlatsFilterOccupationStatuses(state, pageKey),
  );
  const contactIssueTypeStatus = useSelector((state) =>
    selectEnagageFlatsFilterissueContactTypes(state, pageKey),
  );
  const requiresResidentSupportStatus = useSelector((state) =>
    selectEngageFlatsRequiresResidentSupport(state, pageKey),
  );

  const searchValueRef = React.useRef(searchValue);
  const textfieldKeyRef = React.useRef(randomKey());
  if (searchValueRef.current !== searchValue) {
    textfieldKeyRef.current = randomKey();
    searchValueRef.current = searchValue;
  }

  const handleEngageFilter = React.useCallback(
    (filterChanges: Partial<EngageFlatsFilter>) => {
      if (filtersLocked) {
        return;
      }
      dispatch(
        doEngageFlatsSetFilter({
          key: pageKey,
          data: filterChanges,
        }),
      );
      dispatch(
        doEngageFlatsLoad({
          key: pageKey,
          propertyId: property.id,
          page: 'reset',
        }),
      );
    },
    [dispatch, filtersLocked, pageKey, property.id],
  );

  const handleSearch = React.useCallback(
    (newSearchValue: string) => {
      searchValueRef.current = newSearchValue;
      handleEngageFilter({ search: newSearchValue });
    },
    [handleEngageFilter],
  );

  const filterIsTriggered =
    searchValue.length > 0 ||
    selectedOccupationStatuses.length < allOccupationStatusIds.length ||
    contactIssueTypeStatus.length > 0 ||
    requiresResidentSupportStatus.length > 0;

  const handleOpenFiltersModal = React.useCallback(() => {
    if (filtersLocked) {
      return;
    }
    dispatch(
      NAVIGATE_MODAL({
        screen: ModalScreens.EngageFlatsFilter,
        size: 'small',
        params: {
          pageKey,
          propertyId: property.id,
        },
      }),
    );
  }, [dispatch, filtersLocked, pageKey, property.id]);

  return (
    <View>
      <NavigationHeader marginRight={0} marginLeft={0}>
        <SidebarShowButton />
        <ViewWithSeparators
          horizontal
          style={{
            alignItems: 'center',
            flex: 1,
          }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          <Button
            icon={ICONS.ChevronLeftLight}
            text="Engage"
            buttonTheme={ButtonTheme.header}
            onPress={onBackPress}
            style={{
              paddingLeft: theme.paddingHorizontalHalf,
              paddingRight: theme.paddingHorizontal,
            }}
            chipLike
          />
          <Txt weight="bold" numberOfLines={1} style={{ flex: 1 }}>
            {property.name}{' '}
            <Txt
              numberOfLines={1}
              style={{ color: theme.colors.fieldValueText }}
            >
              {isLoading
                ? 'Loading Flats'
                : pluralise(flatsCount, '{x} Flat', '{x} Flats', 'No Flats')}
            </Txt>
          </Txt>
          <Menu
            iconButtonConfig={{ icon: ICONS.EllipsisLight }}
            options={[
              {
                text: 'Export List as CSV',
                icon: ICONS.ArrowDownLight,
              },
              '-',
              {
                text: 'Onboard Flats',
                subText: 'Import new flats into engage',
                onPress: onOnboardFlatsPress,
              },
              '-',
              {
                text: 'Reload',
                icon: ICONS.RotateRightLight,
                onPress: onReloadPress,
              },
            ]}
          />
        </ViewWithSeparators>
      </NavigationHeader>
      <HeaderToolbar
        key={textfieldKeyRef.current}
        searchValue={searchValue}
        onSearch={handleSearch}
        searchPlaceholder="Search flats and contacts"
        searchDisabled={filtersLocked}
        showSearch={!hasBulkSelection}
        leftChildren={
          filtersLocked && hasBulkSelection && onBulkSendPress ? (
            <View style={{ alignItems: 'flex-start' }}>
              <Highlight highlight style={{ borderRadius: 99 }}>
                <Button
                  icon={ICONS.PaperPlaneLight}
                  text="Send Message"
                  onPress={onBulkSendPress}
                  chipLike
                  buttonTheme={ButtonTheme.themeless}
                />
              </Highlight>
            </View>
          ) : undefined
        }
        leftChildrenWithBox
      >
        <IconButton
          icon={ICONS.SlidersUpLight}
          color={
            filtersLocked ? theme.colors.fieldValueText : theme.colors.blue
          }
          onPress={handleOpenFiltersModal}
          showBadge={filterIsTriggered}
          disabled={filtersLocked}
        />
      </HeaderToolbar>
    </View>
  );
};
